import { getStandardizeRewards } from "./games-parameter-history";
import { IHGameParams, IHSelectedMetric, MetricValues, ParticipantObj, ManualResult, StandardizeRewards, RankingParticipant, ActionHistory, HistoricalTeamObj, IHGameStandardize, HTournamentInfo } from './games-parameter.model';
import { GameType } from "./games.model";

const INTERVAL_TYPE = {
    DAY: 0,
    WEEK: 1,
    MONTH: 2
} as const;

// local functions
const defaultLocalization = (key: string, defaultText?: string) => defaultText

// Participants: Game history
// Pure function section
export const isConnect3 = (gameType: number) => gameType === GameType.Connect3;
export const isConnectAll = (gameType: number) => gameType === GameType.Blackout;
export const isTopTeam = (gameType: number) => gameType === GameType.TopTeam;
export const isTopDog = (gameType: number) => gameType === GameType.TopDog;
export const isMilestone = (gameType: number) => gameType === GameType.Milestone;
export const isRaffle = (gameType: number) => gameType === GameType.Raffle;
export const isBracket = (gameType: number) => gameType === GameType.Brackets;

export const getAllMetrics = (game: IHGameParams): IHSelectedMetric[] => {
    if (!game?.HistorySelectedMetrics) return [];
    return game.HistorySelectedMetrics.map(s => {
        if (game.HMetricDetails[s.MetricID]) return {
            ...s,
            MetricDetail: game.HMetricDetails[s.MetricID],
        };
        return s;
    });
};

export const getSingleMetric = (game: IHGameParams) => {
    const allMetrics = getAllMetrics(game);
    const metric = allMetrics[0]; // first metric is never a qualifier
    metric.IsQualifier = false;
    return metric;
};

export const getConnect3Metrics = (game: IHGameParams): IHSelectedMetric[] => {
    if (!game?.HistorySelectedMetrics) return [];
    const metrics = game.HistorySelectedMetrics.filter(m => !m.IsQualifier).map(s => {
        if (game.HMetricDetails[s.MetricID]) return {
            ...s,
            MetricDetail: game.HMetricDetails[s.MetricID],
        };
        return s;
    });
    if (metrics.length < 9) {
        /**
         * 0,1,2
         * 3,4,5 -> free space is always 4
         * 6,7,8
         */
        metrics.splice(4, 0, {
            HistoryID: 0,
            HSelectedMetricsID: 0,
            IsQualifier: false,
            LeftNumber: null,
            MetricID: -1,
            RightNumber: -1,
            Position: 4,
            MetricDetail: {
                Abbrev: 'Free_Space',
                MetricID: -1,
                Name: 'Free_Space',
                ConversionType: 0,
                DefaultMetricDirection: 0
            }
        });
    }
    metrics.sort((a, b) => {
        return a.Position - b.Position;
    });
    return metrics;
};

export const getQualifier = (game: IHGameParams): IHSelectedMetric | undefined => {
    const allMetrics = getAllMetrics(game);
    const qualifiers = allMetrics.filter(m => m.IsQualifier);
    if (isRaffle(game.GameType)) {
        return qualifiers.length === 2 && qualifiers[1];
    }
    return qualifiers[qualifiers.length - 1];
};

export const hasReward = (participant: ParticipantObj) => {
    return participant.calculatedCoins > 0 || participant.calculatedXP > 0 || participant.custom?.length > 0 || participant.badges?.length > 0;
}

export const isQualified = (qualifierMetric: IHSelectedMetric, myValue: number) => {
    let isQualified = false;

    switch (qualifierMetric.ComparerType) {
        case 1: // Greater than
            if (myValue > qualifierMetric.LeftNumber)
                isQualified = true;
            break;
        case 2: // Less than
            if (myValue < qualifierMetric.RightNumber)
                isQualified = true;
            break;
        case 3: // Between
            if (myValue >= qualifierMetric.LeftNumber && myValue <= qualifierMetric.RightNumber)
                isQualified = true;
            break;
        default:
            return false;
    }
    return isQualified;
};

export const getBracketRanking = (game: IHGameStandardize, actionHistory: ActionHistory[], info: HTournamentInfo): ManualResult  => {
    const manualRank: ManualResult = {
        shouldCalculateResult: true,
        ranking: [],
    };
    if (!info.matches.leftSide || !info.matches.rightSide || !info.NumberOfRounds) return manualRank;
    const validRounds = Math.min(info.NumberOfRounds, 4); // final, semi final, quarters and octaves
    const sortValues = <T extends { place?: number }>(a: T, b: T) => {
        return a.place - b.place;
    };

    const allParticipants: RankingParticipant[] = [...info.matches.leftSide.flat(5), ...info.matches.rightSide.flat(5)]
        .filter(m => m.isWinner && m.RoundNumber <= validRounds)
        .map(m => ({
            RelatedID: m.LoginID,
            place: m.RoundNumber
        }));
    allParticipants.sort(sortValues);
    return {
        ...manualRank,
        ranking: allParticipants,
    };
};

export const getRanking = (participants: ParticipantObj[], game: IHGameStandardize, actionHistory: ActionHistory[], teamsInfo: { [key: number]: HistoricalTeamObj } = {}) : ManualResult => {
    const metric = game.metricDefinition;
    const currentDate = new Date();
    currentDate.setHours(0,0,0,0);
    switch(game.IntervalType) {
        case INTERVAL_TYPE.DAY:
            currentDate.addDays(-1 * game.IntervalAmount + 1);
            break;
        case INTERVAL_TYPE.WEEK:
            currentDate.addDays(game.IntervalAmount * -7 + 1);
            break;
        case INTERVAL_TYPE.MONTH:
            currentDate.addMonths(-1 * game.IntervalAmount).addDays(1);
    }
    const firstParticipant = participants[0];
    const direction = metric?.MetricDetail?.DefaultMetricDirection;
    const manualRank: ManualResult = {
        shouldCalculateResult: actionHistory.length === 0 && firstParticipant?.participant?.PeriodStartDate && new Date(firstParticipant.participant.PeriodStartDate) >= currentDate, // ranking users manually only if there is no actionHistory
        ranking: [],
    }; 

    const sortValues = <T extends { metricValue?: number }>(a: T, b: T) => {
        if (direction) return b.metricValue - a.metricValue;
        return a.metricValue - b.metricValue;
    };

    if (metric?.MetricID > 0 && isTopDog(game.GameType)) {
        const participantsSorts: ParticipantObj[] = participants.flatMap(p => {
            const metricValue = p?.metricsValues?.find(m => m.MetricID === metric.MetricID)
            if (metricValue?.MetricDisplayValue != null && metricValue.MetricValue !== null) {
                return {
                    ...p,
                    metricValue: metricValue.MetricValue,
                };
            }
            return [];
        });
        participantsSorts.sort(sortValues);
        const ranking: RankingParticipant[] = [];
        let currentPlace = 0;
        let previousMetric = null;
        participantsSorts.forEach(p => {
            if (p.metricValue != previousMetric) {
                currentPlace = currentPlace + 1;
            }
            ranking.push({
                RelatedID: p.participant.LoginID,
                place: currentPlace
            });
            previousMetric = p.metricValue;
        });
        const totalRewards = getStandardizeRewards(game).length;
        manualRank.ranking = ranking.filter(r => r.place <= totalRewards);
    }
    if (metric?.MetricID > 0 && isTopTeam(game.GameType)) {
        const teamsSorts: (HistoricalTeamObj & { metricValue?: number })[] = Object.values(teamsInfo).flatMap(t => {
            const metricValue = t.metricsValues?.find(m => m.MetricID === metric.MetricID)
            if (metricValue?.MetricDisplayValue != null && metricValue.MetricValue !== null) {
                return {
                    ...t,
                    metricValue: metricValue.MetricValue,
                };
            }
            return [];
        });
        teamsSorts.sort(sortValues);
        const ranking: RankingParticipant[] = [];
        let currentPlace = 0;
        let previousMetric = null;
        teamsSorts.forEach(t => {
            if (t.metricValue != previousMetric) {
                currentPlace = currentPlace + 1;
            }
            ranking.push({
                RelatedID: t.TeamID,
                place: currentPlace
            });
            previousMetric = t.metricValue;
        });
        const totalRewards = game?.rewards?.length || 0;
        manualRank.ranking = ranking.filter(r => r.place <= totalRewards);
    }    
    return manualRank;
};

// Results functions

/**
 * Connect 3 and connect all values
 * 2^0   2^1   2^2
 * 2^3   2^4   2^5
 * 2^6   2^7   2^8
 * 
 * win cases connect 3
 * 2^0 + 2^1 + 2^2 = 7
 * 2^3 + 2^4 + 2^4 = 56
 * 2^5 + 2^6 + 2^7 = 448
 * 2^0 + 2^3 + 2^6 = 73
 * 2^1 + 2^4 + 2^7 = 146
 * 2^2 + 2^5 + 2^8 = 292
 * 2^2 + 2^4 + 2^6 = 84
 * 2^0 + 2^4 + 2^8 = 273
 * 
 * win cases connect all
 * 2^0 + 2^1 + 2^2 = 7 +
 * 2^3 + 2^4 + 2^4 = 56 +
 * 2^5 + 2^6 + 2^7 = 448 =
 *                   511
 */
const WIN_CASES_CONNECT3 = [7, 56, 448, 73, 146, 292, 84, 273] as const;
const WIN_CASES_BLACKOUT = [511] as const;

const hasRewardPlace = (participant: ParticipantObj, reward: StandardizeRewards) => {
    const coins = reward.coins || 0;
    const xp = reward.xp || 0;
    const badges = reward.badges || 0;
    return Math.abs(participant.calculatedCoins) == coins && Math.abs(participant.calculatedXP) == xp && (!badges && !participant.badges?.length || !!participant.badges?.find(b => b === badges));
};

const meetsMetricCriteria = (ComparisonType: number, value: number, minValue?: number, maxValue?: number): boolean => {
    const ComparerType = {
        1: (): boolean => value >= minValue,
        2: (): boolean => value <= minValue,
        3: (): boolean => value >= minValue && value <= maxValue
    };
    return ComparerType[ComparisonType] && ComparerType[ComparisonType]() || false;
}

const getResultValueForRaffle = (participantObject: ParticipantObj, game: IHGameParams, localizationFn = defaultLocalization): string => {
    if (hasReward(participantObject)) return localizationFn('Winner', 'Winner');
    if (!participantObject.passed) return localizationFn("Disqualified", "Disqualified");
    if (!participantObject.metric) return localizationFn('No_Results', 'No results');
    const metric = getSingleMetric(game);
    if (participantObject.passed && metric?.ComparerType && meetsMetricCriteria(metric.ComparerType, participantObject.metricValue, metric.LeftNumber, metric.RightNumber))
        return localizationFn('Raffle_Participant', 'Raffle Participant');

    return !Number.isNaN(participantObject.metricValue) ? localizationFn('No_Ticket', 'No Ticket') : localizationFn('No_Results', 'No results');
};

const getResultForMultiplier = (participant: ParticipantObj, game: IHGameParams, localizationFn = defaultLocalization) => {
    if (hasReward(participant)) {
        return localizationFn("Winner", "Winner");
    };
    return localizationFn("No_Results", "No Results");
};

const winnerRankWrapper = (manualrank: ManualResult) => {
    
    const isWinnerRank = (participantObj: ParticipantObj, game: IHGameParams, localizationFn = defaultLocalization, shouldCalculateResult = false) => {
        const ranking = manualrank.ranking;
        if (shouldCalculateResult) {
            const participantRank = ranking.find(r => r.RelatedID == participantObj?.participant?.LoginID);
            if (participantRank && participantRank.RelatedID > 0) {
                return getLocalizedTierText(participantRank.place, game.GameType, localizationFn);
            }
        }

        if (!hasReward(participantObj)) {
            return localizationFn("No_Results", "No Results");
        }
    
        const rewards = getStandardizeRewards(game).sort((a, b) => a.position - b.position);
        let result = '';
    
        rewards.forEach((reward, index) => {
            const isWinner = hasRewardPlace(participantObj, reward);
            if (isWinner && !result) {
                participantObj.rank = index + 1;
                result = getLocalizedTierText(index + 1, game.GameType, localizationFn);
            }
        });
    
        return result || localizationFn("No_Results", "No Results");
    }
    return isWinnerRank;
};

const winnerRankWrapperTopTeam = (manualrank: ManualResult) => {

    const isWinnerRank = (participantObj: ParticipantObj, game: IHGameParams, localizationFn = defaultLocalization, shouldCalculateResult = false) => {
        const ranking = manualrank.ranking;
        if (shouldCalculateResult) {
            const participantRank = ranking.find(r => r.RelatedID == participantObj?.participant?.TeamID);
            if (participantRank && participantRank.RelatedID > 0) {
                return getLocalizedTierText(participantRank.place, game.GameType, localizationFn);
            }
        }

        if (!hasReward(participantObj)) {
            return localizationFn("No_Results", "No Results");
        }
    
        const rewards = getStandardizeRewards(game).sort((a, b) => a.position - b.position);
        let result = '';
    
        rewards.forEach((reward, index) => {
            const isWinner = hasRewardPlace(participantObj, reward);
            if (isWinner && !result) {
                participantObj.rank = index + 1;
                result = getLocalizedTierText(index + 1, game.GameType, localizationFn);
                participantObj.results = result;
            }
        });
    
        return result || localizationFn("No_Results", "No Results");
    }
    return isWinnerRank;
};

export const isWinnerConnect3 = (participant: ParticipantObj, _: IHGameParams, localizationFn = defaultLocalization) => {
    const metricCompleted = participant.connect3Metrics;
    if (!metricCompleted) return localizationFn("No_Connections", "No Connections");

    let bin = 0;
    for (let i = 0; i < metricCompleted.length; i++) {
        bin += (metricCompleted[i].metricCompleted) ? Math.pow(2, i) : 0;
    }

    for (let i = 0; i < WIN_CASES_CONNECT3.length; i++) {
        let result = bin & WIN_CASES_CONNECT3[i];
        if (result === WIN_CASES_CONNECT3[i])
            return localizationFn("Connect3", "Connect 3");
    }
    return localizationFn("No_Connections", "No Connections");
}

const isWinnerBlackout = (participant: ParticipantObj, _: IHGameParams, localizationFn = defaultLocalization) => {
    const metricCompleted = participant.connect3Metrics;
    if (!metricCompleted) return localizationFn("Not_All_Connections", "Not All Connections");
    let bin = 0;
    metricCompleted.forEach((m, i) => {
        bin += (m?.metricCompleted) ? Math.pow(2, i) : 0;
    });

    for (let i = 0; i < WIN_CASES_BLACKOUT.length; i++) {
        const result = bin & WIN_CASES_BLACKOUT[i];
        if (result === WIN_CASES_BLACKOUT[i])
            return localizationFn("Connected_All", "Connected All");
    }
    return localizationFn("Not_All_Connections", "Not All Connections");
}

const getCalculateLevelValue = (participant: ParticipantObj, game: IHGameParams, localizationFn = defaultLocalization) => {
    const metrics = getAllMetrics(game).filter(m => !m.IsQualifier);
    if (isMilestone(game.GameType)) metrics.reverse();// start with the last milestone saved 
    const metricValue = participant.metricValue;
    let metCriteriaResult = '';
    metrics.forEach((metric, index) => {
        if (metric.ComparerType && !metCriteriaResult) {
            const meetsCriteria = meetsMetricCriteria(metric.ComparerType, metricValue, metric.LeftNumber, metric.RightNumber);
            if (meetsCriteria) {
                metCriteriaResult = getLocalizedTierText(index + 1, game.GameType, localizationFn);
            }
        }
    });
    return metCriteriaResult || localizationFn("No_Results", "No Results");
}

const isWinnerLevel = (participant: ParticipantObj, game: IHGameParams, localizationFn = defaultLocalization, shouldCalculateResult = false) => {
    if (shouldCalculateResult) {
        return getCalculateLevelValue(participant, game, localizationFn);
    }

    if (!hasReward(participant)) return localizationFn("No_Results", "No Results");

    const rewards = getStandardizeRewards(game);
    if (!isMilestone(game.GameType)) rewards.sort((a, b) => a.position - b.position);
    let result = '';

    if (!result) rewards.forEach((reward, index) => {
        const isWinnerPrize = hasRewardPlace(participant, reward);
        if (isWinnerPrize && !result) {
            result = getLocalizedTierText(index + 1, game.GameType, localizationFn);
        }
    });

    return result || getCalculateLevelValue(participant, game, localizationFn);
}

const getLocalizedRoundText = (stage: number, localizationFn = defaultLocalization) => {
    switch (stage) {
        case 1:
            return localizationFn("Round_1_Final", "Final");
        case 2:
            return localizationFn("Round_2_Semi_Finals", "Semi Finals");
        case 3:
            return localizationFn("Round_3_Quarter", "Quarters");
        case 4:
            return localizationFn("Round_4_Octaves", "Octaves");
        default:
            return localizationFn("No_Wins", "No Wins");
    }
}

const wrapperTournament = (manualrank: ManualResult) => {

    const isWinnerBracketTournament = (participant: ParticipantObj, game: IHGameParams, localizationFn = defaultLocalization, shouldCalculateResult = false) => {
        const ranking = manualrank.ranking;
        if (shouldCalculateResult) {
            const participantRank = ranking.find(r => r.RelatedID == participant?.participant?.LoginID);
            if (participantRank && participantRank.RelatedID > 0) {
                return getLocalizedRoundText(participantRank.place, localizationFn);
            } else if (participant?.metricValue === undefined || participant?.metricValue === null || !participant?.metricsValues) return localizationFn("No_Results", "No Results");
        }
        return localizationFn("No_Wins", "No Wins");
    }
    return isWinnerBracketTournament;
};


export const getBracketTournamentLastRound = (result: string, ranking: number, NumberOfRounds: number, localizationFn = defaultLocalization): number => {
    switch (result) {
        case localizationFn("Round_1_Final", "Final"):
        case localizationFn("Round_2_Semi_Finals", "Semi Finals"):
            return 1;
        case localizationFn("No_Wins", "No Wins"):
            return NumberOfRounds;
        default:
            return ranking - 1;
    }
}

// End results functions

export const getResultsValues = (participant: ParticipantObj, game: IHGameParams, localizationFn = defaultLocalization, manualResult: ManualResult = { ranking: [], shouldCalculateResult: false }) => {
    type ResultsFn = {
        [gameType: number]: (p: ParticipantObj, g: IHGameParams, l: (key: string, defaultText?: string) => string, shouldCalculateResult: boolean) => string;
    }
    const rewardsFn: ResultsFn = {
        [GameType.MultiplierMadness]: getResultForMultiplier,
        [GameType.TopDog]: winnerRankWrapper(manualResult), // HOF, return the function that we need for ranks
        [GameType.Bullseye]: isWinnerLevel,
        [GameType.Milestone]: isWinnerLevel, //isWinnerMilestone,
        [GameType.Connect3]: isWinnerConnect3,
        [GameType.Blackout]: isWinnerBlackout,
        [GameType.TopTeam]: winnerRankWrapperTopTeam(manualResult),
        [GameType.Raffle]: getResultValueForRaffle,
        [GameType.Brackets]: wrapperTournament(manualResult),
    }
    return rewardsFn[game.GameType](participant, game, localizationFn, manualResult.shouldCalculateResult) || '';
}

const getLocalizedTierText = (tier: number, gameType: GameType, localizationFn = defaultLocalization) => {
    const tierOptions = {
        [GameType.TopDog]: {
            1: () => localizationFn("First_Place", "1st Place"),
            2: () => localizationFn("Second_Place", "2nd Place"),
            3: () => localizationFn("Third_Place", "3rd Place"),
        },
        [GameType.TopTeam]: {
            1: () => localizationFn("First_Place", "1st Place"),
            2: () => localizationFn("Second_Place", "2nd Place"),
            3: () => localizationFn("Third_Place", "3rd Place"),
        },
        [GameType.Bullseye]: {
            1: () => localizationFn("Ring_Prize", "Bullseye"),
            2: () => localizationFn("2nd_Ring", "2nd Ring"),
            3: () => localizationFn("3rd_Ring", "3rd Ring"),
        },
        default: {
            [GameType.TopDog]: (tier: number) => `${tier}${localizationFn("Th_Symbol", "th")} ${localizationFn("Place", "Place")}`,
            [GameType.TopTeam]: (tier: number) => `${tier}${localizationFn("Th_Symbol", "th")} ${localizationFn("Place", "Place")}`,
            [GameType.Bullseye]: (tier: number) => `${tier}${localizationFn("Th_Symbol", "th")} ${localizationFn("Ring", "Ring")}`,
            [GameType.Milestone]: (tier: number) => `${localizationFn("Milestone", "Milestone")} ${tier}`,
        }
    };

    const tierValue: string = tierOptions[gameType] && tierOptions[gameType][tier] && tierOptions[gameType][tier]();
    return tierValue || tierOptions.default[gameType] && tierOptions.default[gameType](tier) || '';
};

// functions with side effect
export const setParticipantDefaultValues = (participant: ParticipantObj, gameType: number, localizationFn = (key: string, defaultText?: string) => defaultText) => {
    participant.metric = '---';
    participant.qualifier = '---';
    participant.passed = false;
    participant.results = localizationFn("No_Results", "No Results");
    participant.calculatedCoins = 0;
    participant.calculatedXP = 0;
    participant.custom = '';
    participant.badges = [];

    if (isConnect3(gameType) || isConnectAll(gameType)) {
        participant.connect3Metrics = [];
    }
    return participant;
};

export const setParticipantNoActionsHistory = (participant: ParticipantObj) => {
    participant.calculatedCoins = 0;
    participant.calculatedXP = 0;
    participant.badges = [];
    participant.isReverted = false;
    participant.reversionDate = null;
    participant.custom = '';
    return participant;
};

export const setActionItem = (participant: ParticipantObj, actionItem: any) => {
    participant.calculatedCoins += (actionItem.ActionType == 0 || actionItem.ActionType == 6) ? actionItem.NumericValue : 0;
    participant.calculatedXP += (actionItem.ActionType == 1 || actionItem.ActionType == 7) ? actionItem.NumericValue : 0;
    if (actionItem.ActionType == 2 && actionItem.NumericValue) {
        participant.badges.push(actionItem.NumericValue);
    }

    if (actionItem.ActionType == 10 && actionItem.TextValue && actionItem.TextValue.length > 0) { // raffle game
        participant.custom = actionItem.TextValue;
        participant.customRewardValue = actionItem.NumericValue;
        participant.productState = actionItem.NumericValue || -1;
    }

    participant.isReverted = actionItem.Reverted;
    participant.paidDate = actionItem.ActionDate;
    participant.reversionDate = actionItem.RevertedDate;
    return participant;
};

export const setPaymentDate = (participant: ParticipantObj, localizationFn = defaultLocalization, transformDateFn = (value: Date | string | number, format?: string) => value) => {
    if (participant.isReverted === null || participant.isReverted === undefined) {
        participant.paymentDate = "";
        return participant;
    }

    if (participant.calculatedCoins == 0 && participant.calculatedXP == 0) {
        participant.paymentDate = "---";
        return participant;
    }

    if (!participant.isReverted) {
        participant.paymentDate = localizationFn("Paid_on", "Paid on") + " " + transformDateFn(participant.paidDate, CVAngularDateFormat);
        return participant;
    }

    participant.paymentDate = localizationFn("Reverted_on", "Reverted on") + " " + transformDateFn(participant.reversionDate, CVAngularDateFormat);
    return participant;
};

export const setQualifier = (participant: any, qualifierMetric: IHSelectedMetric) => {
    if (!qualifierMetric) {
        participant.qualifier = '---';
        participant.qualifierValue = 0;
        participant.passed = true; // if there is no qualifier, by default passed = true;
        return participant;
    }
    const qualifierValue = participant.metricsValues?.find(m => m.MetricID == qualifierMetric.MetricID);
    if (!qualifierValue || qualifierValue.MetricDisplayValue === null) {
        participant.qualifier = '---';
        participant.qualifierValue = 0;
        participant.passed = false;
        return participant;
    }
    participant.qualifier = qualifierValue.MetricDisplayValue;
    participant.qualifierValue = qualifierValue.MetricValue;

    participant.passed = isQualified(qualifierMetric, qualifierValue.MetricValue);
    return participant;
};

export const setConnect3Positions = (participant: ParticipantObj, metrics: IHSelectedMetric[]) => {
    const connect3Metrics: MetricValues[] = new Array(9).fill(null);
    const metricCompleted: boolean[] = new Array(9).fill(null);

    if (metrics.length < 9) {
        metricCompleted[4] = true;
        connect3Metrics[4] = {
            MetricID: 0,
            MetricValue: 0,
            MetricDisplayValue: '---',
            metricCompleted: true,
            RelatedID: participant.participant.LoginID,
            MetricValueID: 0
        };
    } //Free Space = true

    //Sort the Positions
    metrics.sort((a, b) => {
        return a.Position - b.Position;
    });

    metrics.forEach((metricPosition) => {
        const metric: MetricValues = participant.metricsValues?.find(m => m.MetricID == metricPosition.MetricID);

        if (metric) {
            metric.metricCompleted = false;
            connect3Metrics[metricPosition.Position] = metric;
        } else {
            connect3Metrics[metricPosition.Position] = {
                MetricID: metricPosition.MetricID,
                MetricValue: 0,
                MetricDisplayValue: '---',
                metricCompleted: false,
                RelatedID: participant.participant.LoginID,
                MetricValueID: 0
            };
        }

        if (metricPosition.MetricID > -1 && metric != null) {
            metricCompleted[metricPosition.Position] = metricPosition.MetricDetail.DefaultMetricDirection === 0 ? metric.MetricValue < metricPosition.LeftNumber : metric.MetricValue > metricPosition.LeftNumber;
            connect3Metrics[metricPosition.Position].metricCompleted = metricCompleted[metricPosition.Position];
        }
    });
    participant.connect3Metrics = connect3Metrics;
    return participant;
};
